import { addDays, format, subDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { DateRange, Matcher } from 'react-day-picker';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

import { defaultShortCuts } from '../../constants/datepickerDefaultShortCuts';
import { cn } from '../../utils/cn';
import { formatDate, removeTimeZone } from '../../utils/formatDate';
import { getDateRange } from '../../utils/getDateRange';
import { Calendar, ShortCut } from './Calendar';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';

interface ControlledDateRangePickerProps {
	startDateName: string;
	endDateName: string;
	title: string;
	startFrom?: Date;
	minDate?: Date;
	maxDate?: Date;
	disabledDates?: {
		startDate: Date;
		endDate: Date;
	}[];
	showShortCuts?: boolean;
	showClearButton?: boolean;
	shortCuts?: Array<ShortCut>;
	className?: {
		button?: string;
		p?: string;
	};
}

export function ControlledDateRangePicker({
	startDateName,
	endDateName,
	title,
	startFrom,
	minDate,
	maxDate,
	disabledDates = [],
	showShortCuts = false,
	showClearButton = true,
	shortCuts,
}: ControlledDateRangePickerProps) {
	const { watch, setValue } = useFormContext();

	const startDateValue = watch(startDateName);
	const endDateValue = watch(endDateName);

	const getDisabledDates = (): Matcher[] => {
		const datesToBeDisabled = [] as Matcher[];
		if (minDate) {
			datesToBeDisabled.push({
				before: minDate,
			});
		}
		if (maxDate) {
			datesToBeDisabled.push({
				after: maxDate,
			});
		}
		disabledDates.forEach((disabledDate) => {
			datesToBeDisabled.push({
				after: subDays(disabledDate.startDate, 1),
				before: addDays(disabledDate.endDate, 1),
			});
		});
		return datesToBeDisabled;
	};

	const parseFormDateValue = (value: string | Date): Date =>
		removeTimeZone(new Date(value));

	const getDatePickerValue = (): DateRange | undefined => {
		if (!startDateValue) return undefined;

		const startDate = parseFormDateValue(startDateValue);
		const endDate = endDateValue
			? parseFormDateValue(endDateValue)
			: undefined;

		return {
			from: startDate,
			to: endDate,
		};
	};

	const datePickerValue = getDatePickerValue();
	const [isHovered, setIsHovered] = useState(false);

	const handleSelect = (dateRange: DateRange | undefined) => {
		if (dateRange?.from && dateRange?.to) {
			const range = getDateRange(dateRange.from, dateRange.to);
			const hasDisabledDates = range.some((date) => {
				return disabledDates.find(
					(disabled) =>
						format(disabled.startDate, 'yyyy-MM-dd') ===
						format(date, 'yyyy-MM-dd'),
				);
			});
			if (hasDisabledDates) {
				return;
			}
		}

		const newStartDate = dateRange?.from
			? formatDate(removeTimeZone(dateRange.from))
			: undefined;
		const newEndDate = dateRange?.to
			? formatDate(removeTimeZone(dateRange.to))
			: undefined;

		setValue(
			startDateName,
			newStartDate || (dateRange?.from ? dateRange.from : undefined),
		);
		setValue(
			endDateName,
			newEndDate || (newStartDate && !newEndDate ? newStartDate : newEndDate),
		);
	};

	const displayValue = datePickerValue?.from
		? datePickerValue.to
			? `${format(datePickerValue.from, 'dd/MM/yyyy', { locale: ptBR })} - ${format(datePickerValue.to, 'dd/MM/yyyy', { locale: ptBR })}`
			: format(datePickerValue.from, 'dd/MM/yyyy', { locale: ptBR })
		: 'dd/mm/aaaa - dd/mm/aaaa';

	return (
		<div>
			<div className="mb-1 text-[12px] text-[#4A4A4A]">{title}</div>
			<Popover>
				<PopoverTrigger asChild>
					<button
						type="button"
						className={cn(
							'w-full h-8 text-xs py-1.5 px-3.5 flex items-center justify-between',
							'border border-[#C4C4C4] hover:border-[#9CA3AF] rounded bg-transparent cursor-pointer text-left',
							datePickerValue?.from ? 'text-[#4A4A4A]' : 'text-[#9CA3AF]',
						)}
					>
						<span>{displayValue}</span>
						<CalendarTodayIcon
							sx={{
								fontSize: '20px',
								color: '#757575',
								pointerEvents: 'none',
							}}
							aria-hidden="true"
						/>
					</button>
				</PopoverTrigger>
				<PopoverContent
					className={cn(
						'z-[10000] w-auto p-0',
						(showShortCuts || showClearButton) && '-translate-x-20',
					)}
					align="start"
				>
					<Calendar
						initialFocus
						mode="range"
						defaultMonth={startFrom ?? datePickerValue?.from}
						selected={datePickerValue}
						onSelect={handleSelect}
						numberOfMonths={2}
						className="bg-white"
						shortCuts={shortCuts || defaultShortCuts}
						showShortCuts={showShortCuts}
						showClearButton={showClearButton}
						disabled={getDisabledDates()}
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
}
